/* Persistent desk prognosis marks drawn inside the PriceChart SVG.
   The call stays put until the desk decides an adjustment is in order. */

const fmtP = (v, d) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const decOf = (v) => (Math.abs(v) >= 1000 ? 2 : 4);

const held = (ts) => {
  if (!ts) return "";
  const s = Math.max(0, Math.round(Date.now() / 1000 - ts));
  return s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${(s / 3600).toFixed(1)}h`;
};

const PositionMarks = ({ m, view, d }) => {
  const inRange = (v) => v != null && v >= view.lo && v <= view.hi;
  const yOf = (v) => view.y(Math.max(view.lo, Math.min(view.hi, v)));
  const long = m.side === "LONG";
  const col = long ? "#00E5B5" : "#FF3355";
  const x0 = view.plotW * 0.35;
  const line = (price, stroke, dash, label, anchorUp, key, width = 1, testid, cls) => inRange(price) && (
    <g key={key} data-testid={testid} className={cls}>
      <line x1={x0} x2={view.plotW} y1={yOf(price)} y2={yOf(price)} stroke={stroke} strokeWidth={width} strokeDasharray={dash} opacity="0.9" vectorEffect="non-scaling-stroke" />
      <text x={x0 + 4} y={yOf(price) + (anchorUp ? -3 : 9)} fill={stroke} fontSize="7.5" fontWeight="700" fontFamily="JetBrains Mono">{label}</text>
    </g>
  );
  const rTxt = (r) => `${r >= 0 ? "+" : ""}${Number(r).toFixed(2)}R`;
  return (
    <g data-testid="position-marks" pointerEvents="none">
      {line(m.initialStop, "#71717a", "2 3", `INITIAL STOP ${fmtP(m.initialStop, d)}`, !long, "is")}
      {line(m.currentStop, "#f59e0b", "6 3", `CURRENT STOP ${fmtP(m.currentStop, d)} · locked ${rTxt(m.lockedR)}`, !long, "cs", 1.4, "pme-current-stop-line")}
      {m.targets.map((tp, i) => line(tp, m.tpStatus[i]?.status === "HIT" ? "#34d399" : "#22d3ee", m.tpStatus[i]?.status === "HIT" ? "" : "4 3",
        `TP${i + 1} ${fmtP(tp, d)} · ${m.tpStatus[i]?.status === "HIT" ? "HIT" : `${Math.round(m.tpStatus[i]?.progress || 0)}%`}`, long, `tp${i}`, m.tpStatus[i]?.status === "APPROACHING" ? 1.6 : 1, undefined, m.tpStatus[i]?.status === "APPROACHING" ? "r2-tp-pulse" : undefined))}
      {m.scaleIns.map((s, i) => line(s.price, "#e879f9", "1 2", `SCALE-IN ${s.qty}x @ ${fmtP(s.price, d)}${s.authorized ? "" : " (shadow)"}`, long, `si${i}`))}
      {inRange(m.averageEntry) && (
        <g filter="url(#glow)" data-testid="pme-entry-line">
          <line x1={x0} x2={view.plotW} y1={yOf(m.averageEntry)} y2={yOf(m.averageEntry)} stroke={col} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
          <g transform={`translate(${x0 + 4}, ${yOf(m.averageEntry) + (long ? 4 : -26)})`}>
            <rect x={0} y={0} width={230} height={22} rx="3" fill="rgba(6,10,16,0.9)" stroke={col} strokeOpacity="0.6" />
            <text x={5} y={9.5} fill={col} fontSize="8" fontWeight="800" fontFamily="JetBrains Mono">
              {m.scaleIns.length ? "AVG " : ""}ENTRY {fmtP(m.averageEntry, d)} · {m.side} {m.quantity}x · {rTxt(m.currentR)}
            </text>
            <text x={5} y={18.5} fill="#8b93a1" fontSize="7" fontFamily="JetBrains Mono">
              {m.tradeState.replace(/_/g, " ")} · open risk ${Math.round(m.openRiskUsd)} · {m.runnerQty != null ? `runner ${m.runnerQty}x` : m.shadow ? "shadow" : "managed"}
            </text>
          </g>
        </g>
      )}
    </g>
  );
};

const FollowMarks = ({ f, view, d }) => {
  const inRange = (v) => v != null && v >= view.lo && v <= view.hi;
  const yOf = (v) => view.y(Math.max(view.lo, Math.min(view.hi, v)));
  const rows = [...(f.resistance || []).map((v) => [v, "#FF3355", "R"]), ...(f.support || []).map((v) => [v, "#00E5B5", "S"]),
    ...(f.invalidation != null ? [[f.invalidation, "#f59e0b", "INVAL"]] : []), ...(f.liquidity || []).map((t) => [t.level, "#22d3ee", t.status === "swept" ? "SWEPT" : "LIQ"])];
  const name = String(f.agent || "").replace(/_agent$/, "").replace(/_/g, " ").toUpperCase();
  return (
    <g data-testid="follow-marks" pointerEvents="none">
      <rect x={0} y={0} width={view.plotW} height={PLOTH_FALLBACK(view)} fill="rgba(56,189,248,0.03)" />
      {rows.filter(([v]) => inRange(Number(v))).map(([v, c, tag], i) => (
        <g key={i} filter="url(#glow)">
          <line x1={0} x2={view.plotW} y1={yOf(Number(v))} y2={yOf(Number(v))} stroke={c} strokeWidth="1.3" strokeDasharray="10 4" opacity="0.9" vectorEffect="non-scaling-stroke" />
          <text x={6} y={yOf(Number(v)) - 3} fill={c} fontSize="7.5" fontWeight="800" fontFamily="JetBrains Mono">{name} · {tag} {fmtP(Number(v), d)}</text>
        </g>
      ))}
    </g>
  );
};
const PLOTH_FALLBACK = (view) => view.y(view.lo);

export const DeskMarks = ({ prognosis, view, bars, PADT, PLOTH, trend, deskOrder, positions, follow }) => {
  if (!view) return null;
  const inRange = (v) => v != null && v >= view.lo && v <= view.hi;
  const yOf = (v) => view.y(Math.max(view.lo, Math.min(view.hi, v)));
  const followMarks = follow ? <FollowMarks f={follow} view={view} d={decOf(view.hi)} /> : null;
  const posMarks = (positions || []).map((m) => <PositionMarks key={m.key} m={m} view={view} d={Math.abs(m.averageEntry) >= 1000 ? 2 : 4} />);
  const d = decOf((prognosis && prognosis.price_at) || view.hi);
  const barX = (ts) => {
    let idx = -1;
    for (let i = bars.length - 1; i >= 0; i--) { if ((bars[i].ts || 0) <= ts) { idx = i; break; } }
    return idx >= 0 ? Math.max(0, view.xOfGlobal(idx)) : 0;
  };

  const trendMarks = trend && (
    <g data-testid="trend-marks" pointerEvents="none">
      {trend.impulse && inRange(trend.impulse.from) && inRange(trend.impulse.to) && (
        <line x1={barX(trend.impulse.from_ts)} y1={yOf(trend.impulse.from)} x2={barX(trend.impulse.to_ts)} y2={yOf(trend.impulse.to)}
          stroke={trend.bias === "bullish" ? "#00E5B5" : "#FF3355"} strokeWidth="1.2" strokeDasharray="6 3" opacity="0.55" vectorEffect="non-scaling-stroke" />
      )}
      {(trend.entry_targets || []).map((t, i) => {
        if (!(inRange(t.low) || inRange(t.high))) return null;
        const y1 = yOf(t.high), y2 = yOf(t.low);
        const x0 = view.plotW * 0.7;
        return (
          <g key={`tt${i}`} data-testid={`trend-target-${t.type}`}>
            <rect x={x0} y={y1} width={view.plotW - x0} height={Math.max(2, y2 - y1)} fill="rgba(34,211,238,0.10)" stroke="#22d3ee" strokeOpacity="0.55" strokeDasharray="3 3" />
            <text x={x0 + 4} y={y1 - 3} fill="#67e8f9" fontSize="7.5" fontWeight="700" fontFamily="JetBrains Mono">
              {t.type === "fvg" ? "FVG" : "OB"} TARGET {fmtP(t.level, d)}{t.distance_atr != null ? ` · ${t.distance_atr} ATR` : ""}
            </text>
          </g>
        );
      })}
    </g>
  );

  const orderMarks = deskOrder && deskOrder.status === "WORKING" && (() => {
    const o = deskOrder;
    const long = o.direction === "LONG";
    const col = long ? "#00E5B5" : "#FF3355";
    const x0 = view.plotW * 0.55;
    const ye = yOf(o.entry), ys = yOf(o.stop_loss), yt = yOf(o.take_profit);
    const left = Math.max(0, Math.round(o.ttl_bars - (Date.now() / 1000 - o.placed_ts) / o.bar_secs));
    return (
      <g data-testid="desk-order-marks" pointerEvents="none">
        {inRange(o.entry) && (inRange(o.stop_loss) || inRange(o.take_profit)) && (
          <>
            <rect x={x0} y={Math.min(ye, ys)} width={view.plotW - x0} height={Math.abs(ys - ye)} fill="rgba(255,51,85,0.08)" />
            <rect x={x0} y={Math.min(ye, yt)} width={view.plotW - x0} height={Math.abs(yt - ye)} fill="rgba(0,229,181,0.08)" />
          </>
        )}
        {inRange(o.entry) && (
          <g filter="url(#glow)">
            <line x1={x0} x2={view.plotW} y1={ye} y2={ye} stroke={col} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
            <g transform={`translate(${x0 + 4}, ${ye + (long ? 4 : -24)})`}>
              <rect x={0} y={0} width={196} height={20} rx="3" fill="rgba(6,10,16,0.9)" stroke={col} strokeOpacity="0.6" />
              <text x={5} y={9} fill={col} fontSize="8" fontWeight="800" fontFamily="JetBrains Mono" data-testid="desk-order-label">
                DESK LIMIT {o.direction} {o.qty}x @ {fmtP(o.entry, d)}
              </text>
              <text x={5} y={17} fill="#8b93a1" fontSize="7" fontFamily="JetBrains Mono">
                {o.kind} · {o.rr}R · {Math.round(o.confidence)}% · {left} bars left · {o.mode}
              </text>
            </g>
          </g>
        )}
        {inRange(o.stop_loss) && (
          <>
            <line x1={x0} x2={view.plotW} y1={ys} y2={ys} stroke="#FF3355" strokeWidth="1" strokeDasharray="4 3" opacity="0.8" vectorEffect="non-scaling-stroke" />
            <text x={view.plotW - 4} y={ys + (long ? 9 : -3)} textAnchor="end" fill="#FF3355" fontSize="7.5" fontWeight="700" fontFamily="JetBrains Mono">DESK SL {fmtP(o.stop_loss, d)}</text>
          </>
        )}
        {inRange(o.take_profit) && (
          <>
            <line x1={x0} x2={view.plotW} y1={yt} y2={yt} stroke="#00E5B5" strokeWidth="1" strokeDasharray="4 3" opacity="0.8" vectorEffect="non-scaling-stroke" />
            <text x={view.plotW - 4} y={yt + (long ? -3 : 9)} textAnchor="end" fill="#00E5B5" fontSize="7.5" fontWeight="700" fontFamily="JetBrains Mono">DESK TP {fmtP(o.take_profit, d)}</text>
          </>
        )}
      </g>
    );
  })();

  if (!prognosis) return <g>{followMarks}{trendMarks}{orderMarks}{posMarks}</g>;
  const p = prognosis;
  const bull = p.direction === "bullish", bear = p.direction === "bearish";
  const col = p.status === "invalidated" ? "#71717a" : bull ? "#00E5B5" : bear ? "#FF3355" : "#a1a1aa";

  // anchor bar = last bar at or before the call
  const ax = barX(p.anchor_ts);
  const ay = p.price_at != null ? yOf(p.price_at) : null;
  const offscreen = p.price_at != null && !inRange(p.price_at);
  const arrow = bull ? "▲" : bear ? "▼" : "•";
  const label = `${p.status === "invalidated" ? "INVALIDATED" : "DESK"} ${arrow} ${String(p.direction || "").toUpperCase()} ${Math.round(p.confidence || 0)}%`;
  const sub = p.status === "invalidated" ? "re-analysing" : `held ${held(p.anchor_ts)} · v${p.version}`;
  const labelY = ay == null ? PADT + 30 : offscreen ? (p.price_at > view.hi ? PADT + 4 : PADT + PLOTH - 26) : ay - 26;

  return (
    <g data-testid="desk-marks" pointerEvents="none">
      {followMarks}
      {trendMarks}
      {orderMarks}
      {posMarks}
      {/* directional call carried forward from the anchor bar (label clamped to the plot edge when off-screen) */}
      {ay != null && (
        <g>
          {!offscreen && (
            <>
              <line x1={ax} x2={view.plotW} y1={ay} y2={ay} stroke={col} strokeWidth="1.2" strokeDasharray="2 4" opacity="0.75" vectorEffect="non-scaling-stroke" />
              <line x1={ax} x2={ax} y1={ay - 14} y2={ay + 14} stroke={col} strokeWidth="1.6" opacity="0.9" vectorEffect="non-scaling-stroke" />
              <path d={`M ${ax} ${ay} m -5 0 l 5 -5 l 5 5 l -5 5 z`} fill={col} filter="url(#glow)" />
            </>
          )}
          <g transform={`translate(${Math.min(ax + 8, view.plotW - 150)}, ${labelY})`}>
            <rect x={0} y={0} width={142} height={22} rx="3" fill="rgba(6,10,16,0.88)" stroke={col} strokeOpacity="0.55" />
            <text x={6} y={9.5} fill={col} fontSize="8.5" fontWeight="800" fontFamily="JetBrains Mono" data-testid="desk-call-label">{label}</text>
            <text x={6} y={18.5} fill="#8b93a1" fontSize="7" fontFamily="JetBrains Mono">{sub} · @{fmtP(p.price_at, d)}{offscreen ? (p.price_at > view.hi ? " ↑" : " ↓") : ""}</text>
          </g>
        </g>
      )}

      {/* invalidation */}
      {inRange(p.invalidation) && (() => {
        const yy = yOf(p.invalidation);
        return (
          <g filter="url(#glow)" data-testid="desk-invalidation">
            <line x1={ax} x2={view.plotW} y1={yy} y2={yy} stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="8 4" opacity="0.85" vectorEffect="non-scaling-stroke" />
            <text x={view.plotW - 4} y={yy + (bull ? 10 : -4)} textAnchor="end" fill="#f59e0b" fontSize="8.5" fontWeight="700" fontFamily="JetBrains Mono">
              DESK INVALIDATION {fmtP(p.invalidation, d)}
            </text>
          </g>
        );
      })()}

      {/* target */}
      {inRange(p.target) && p.status !== "invalidated" && (() => {
        const yy = yOf(p.target);
        return (
          <g filter="url(#glow)" data-testid="desk-target">
            <line x1={ax} x2={view.plotW} y1={yy} y2={yy} stroke={col} strokeWidth="1.1" strokeDasharray="10 5" opacity="0.7" vectorEffect="non-scaling-stroke" />
            <text x={view.plotW - 4} y={yy + (bull ? -4 : 10)} textAnchor="end" fill={col} fontSize="8.5" fontWeight="700" fontFamily="JetBrains Mono">
              DESK TARGET {fmtP(p.target, d)}
            </text>
          </g>
        );
      })()}

      {/* liquidity agent marks (labels staggered so nearby levels stay legible) */}
      {(() => {
        const items = (p.liquidity_targets || []).filter((t) => inRange(t.level))
          .map((t) => ({ ...t, yy: yOf(t.level) })).sort((a, b) => a.yy - b.yy);
        let lastLabelY = -Infinity;
        return items.map((t, i) => {
          const yy = t.yy;
          let ly = yy - 3;
          if (ly - lastLabelY < 9) ly = lastLabelY + 9;
          lastLabelY = ly;
          const swept = t.status === "swept";
          const c = swept ? "#eab308" : "#22d3ee";
          const x0 = view.plotW * 0.62;
          const up = t.side === "buy-side";
          const rb = t.reversal_bias === "bullish" ? "▲" : t.reversal_bias === "bearish" ? "▼" : "";
          const txt = swept ? `SWEPT → REV ${rb} ${fmtP(t.level, d)}` : `LIQ ${up ? "▲" : "▼"} ${up ? "BUY" : "SELL"}-SIDE ${fmtP(t.level, d)} · UPCOMING`;
          return (
            <g key={`lq${i}`} data-testid={`desk-liq-${t.status}`}>
              <line x1={x0} x2={view.plotW} y1={yy} y2={yy} stroke={c} strokeWidth={swept ? 1.4 : 1} strokeDasharray={swept ? "3 2" : "1 3"} opacity="0.9" vectorEffect="non-scaling-stroke" />
              <circle cx={x0} cy={yy} r="2.2" fill={c} />
              {ly !== yy - 3 && <line x1={x0 + 3} x2={x0 + 5} y1={yy} y2={ly + 1} stroke={c} strokeWidth="0.6" opacity="0.6" />}
              <text x={x0 + 6} y={ly} fill={c} fontSize="7.5" fontWeight="700" fontFamily="JetBrains Mono">{txt}</text>
            </g>
          );
        });
      })()}
    </g>
  );
};
